import React from 'react';
import { AlertCircle, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';

const ReviewStep = (ctx) => {
  const {
    numberOfPackages,
    agreementAccepted, setAgreementAccepted,
    errors,
    isSubscription,
    subscriptionUsage,
    breakdown,
    totalCost,
    duplicateWarning, setDuplicateWarning,
    handleBackStep,
  } = ctx;

  return (
    <>
            {/* Critical Identification Notice */}
            <div style={{ 
              marginTop: '1.5rem',
              marginBottom: '1.5rem',
              padding: '1.25rem',
              backgroundColor: '#fef2f2',
              borderRadius: '8px',
              border: '3px solid #dc2626',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <AlertCircle size={24} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <h3 style={{ 
                    fontSize: '1.1rem', 
                    fontWeight: '700', 
                    color: '#991b1b',
                    marginBottom: '0.5rem' 
                  }}>
                    ⚠️ CRITICAL: Package Identification Required
                  </h3>
                  <p style={{ fontSize: '0.95rem', color: '#7f1d1d', lineHeight: '1.6', margin: 0 }}>
                    <strong>For shipping label packages:</strong> Write the last 6 digits of your tracking number clearly on the outside of each box.
                  </p>
                  <p style={{ fontSize: '0.95rem', color: '#7f1d1d', lineHeight: '1.6', marginTop: '0.5rem', marginBottom: 0 }}>
                    <strong>For QR code packages:</strong> Write the ReturnPal ID (provided after booking) clearly on the outside of each box.
                  </p>
                  <p style={{ 
                    fontSize: '0.9rem', 
                    color: '#991b1b', 
                    marginTop: '0.75rem',
                    padding: '8px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '4px',
                    fontWeight: '600',
                    marginBottom: 0
                  }}>
                    ⚠️ Packages without identification may not be accepted.
                  </p>
                </div>
              </div>
            </div>

            {/* Pricing Summary */}
            <div className="pricing-summary" style={{
              marginTop: '1.5rem',
              padding: '1.5rem',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '2px solid #e5e7eb'
            }}>
              <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem', fontWeight: '600' }}>Pricing Summary</h3>
              <div className="pricing-details">
                {isSubscription && subscriptionUsage && subscriptionUsage.pickups_used_this_month < 2 ? (
                  <>
                    <div className="pricing-line" style={{ color: '#10b981' }}>
                      <span>✓ Subscription Pickup ({numberOfPackages} {numberOfPackages === 1 ? 'package' : 'packages'})</span>
                      <span>Included</span>
                    </div>
                  </>
                ) : breakdown.additionalPickupFee > 0 ? (
                  <div className="pricing-line" style={{ color: '#f59e0b' }}>
                    <span>Additional Pickup ({numberOfPackages} {numberOfPackages === 1 ? 'package' : 'packages'})</span>
                    <span>${breakdown.additionalPickupFee.toFixed(2)}</span>
                  </div>
                ) : (
                  <div className="pricing-line">
                    <span>Pickup ({numberOfPackages} {numberOfPackages === 1 ? 'package' : 'packages'})</span>
                    <span>${breakdown.baseTier.toFixed(2)}</span>
                  </div>
                )}
                {breakdown.additionalPackageFee > 0 && (
                  <div className="pricing-line" style={{ color: '#f59e0b' }}>
                    <span>📦 Additional packages</span>
                    <span>${breakdown.additionalPackageFee.toFixed(2)}</span>
                  </div>
                )}
                {breakdown.packagingCost > 0 && (
                  <div className="pricing-line" style={{ color: '#059669' }}>
                    <span>📦 Packaging materials</span>
                    <span>${breakdown.packagingCost.toFixed(2)}</span>
                  </div>
                )}
                {breakdown.oversizedFee > 0 && (
                  <div className="pricing-line" style={{ color: '#dc2626' }}>
                    <span>📏 Oversized handling fee</span>
                    <span>${breakdown.oversizedFee.toFixed(2)}</span>
                  </div>
                )}
                {breakdown.rushFee > 0 && (
                  <div className="pricing-line" style={{ color: '#f59e0b' }}>
                    <span>⚡ Rush Drop-Off</span>
                    <span>${breakdown.rushFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="pricing-line pricing-total" style={{ 
                  marginTop: '0.75rem', 
                  paddingTop: '0.75rem', 
                  borderTop: '2px solid #d1d5db' 
                }}>
                  <strong style={{ fontSize: '1.1rem' }}>Total Cost</strong>
                  <strong style={{ fontSize: '1.2rem', color: '#10b981' }}>${totalCost.toFixed(2)}</strong>
                </div>
              </div>
            </div>

            {/* Prohibited Items Section */}
            <div style={{ 
              marginTop: '2rem',
              marginBottom: '2rem',
            }}>
              <h3 style={{ 
                fontSize: '1.2rem', 
                fontWeight: '600', 
                color: '#991b1b',
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                🚫 ReturnPal Prohibited Items
              </h3>
              <img 
                src="https://customer-assets.emergentagent.com/job_returnpal-pickup/artifacts/4bk5tqex_image.png"
                alt="ReturnPal Prohibited Items"
                style={{
                  width: '100%',
                  maxWidth: '1600px',
                  height: 'auto',
                  display: 'block',
                  margin: '0 auto'
                }}
              />
            </div>

            {/* Agreement & Acknowledgment — replaces old terms checkbox */}
            <div
              data-testid="agreement-block"
              style={{
                marginTop: '1.5rem',
                marginBottom: '1.5rem',
                padding: '1.25rem',
                backgroundColor: agreementAccepted ? '#ecfdf5' : '#f9fafb',
                border: `2px solid ${agreementAccepted ? '#10b981' : '#d1d5db'}`,
                borderRadius: '8px',
              }}
            >
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={agreementAccepted}
                  onChange={(e) => setAgreementAccepted(e.target.checked)}
                  data-testid="agreement-checkbox"
                  style={{ marginTop: '0.25rem', cursor: 'pointer', width: '20px', height: '20px', flexShrink: 0 }}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937', marginBottom: '0.5rem' }}>
                    Agreement &amp; Acknowledgment
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#374151', lineHeight: 1.6, margin: 0 }}>
                    I have read and agree to ReturnPal&apos;s{' '}
                    <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#10b981', textDecoration: 'underline', fontWeight: 600 }}>Terms of Service</a>
                    {' '}and{' '}
                    <a href="/terms-and-conditions" target="_blank" rel="noreferrer" style={{ color: '#10b981', textDecoration: 'underline', fontWeight: 600 }}>Terms &amp; Conditions</a>.
                    {' '}I understand that ReturnPal is an independent logistics convenience service, is not affiliated with retailers or carriers, and does not determine return eligibility, acceptance, or refund decisions.
                    <br /><br />
                    I acknowledge that I am responsible for verifying retailer return instructions and selecting the correct carrier, label, or QR code.
                  </p>
                </div>
              </label>
              {errors.agreement && (
                <span className="error-message" style={{ display: 'block', marginTop: '0.5rem' }}>{errors.agreement}</span>
              )}
            </div>

            {/* Duplicate-booking soft warning */}
            {duplicateWarning && (
              <div
                data-testid="duplicate-warning-banner"
                style={{
                  padding: '1rem 1.25rem',
                  marginTop: '1rem',
                  backgroundColor: '#fffbeb',
                  border: '2px solid #f59e0b',
                  borderRadius: '8px',
                }}
              >
                <div style={{ fontWeight: 700, color: '#92400e', marginBottom: '0.4rem' }}>
                  ⚠️ Heads up — you already have a recent booking
                </div>
                <div style={{ fontSize: '0.875rem', color: '#78350f', marginBottom: '0.75rem' }}>
                  We found booking <strong>#{duplicateWarning.confirmation_number}</strong> for{' '}
                  <strong>{duplicateWarning.pickup_date}</strong> ({duplicateWarning.pickup_window}) created within the last hour.
                  Are you sure you want to create another one?
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <Button
                    type="submit"
                    data-testid="confirm-duplicate-btn"
                    style={{ backgroundColor: '#dc2626', color: 'white', fontWeight: 600 }}
                  >
                    Yes, continue anyway
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDuplicateWarning(null)}
                    data-testid="cancel-duplicate-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <Button
          type="button"
          onClick={handleBackStep}
          data-testid="wizard-back-btn"
          variant="outline"
          className="btn-large"
          style={{ minWidth: '140px' }}
        >
          ← Back
        </Button>
        <Button type="submit" data-testid="continue-to-payment-btn" className="btn-primary btn-large" style={{ flex: 1, minWidth: '200px' }}>
          Continue to Payment
        </Button>
      </div>
    </>
  );
};

export default ReviewStep;
